import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { History, Send, RefreshCw, BarChart3, Info, FileText, Layout, Users, CheckCircle, XCircle, TrendingUp, Activity, Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { toast } from 'sonner'
import { whatsappApi } from '../api/whatsapp'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import type { Envio } from '../types'

export function HistorialWhatsApp() {
  const [envios, setEnvios] = useState<Envio[]>([])
  const [loading, setLoading] = useState(true)
  const [filtros, setFiltros] = useState({
    search: '',
    estado: '',
    page: 1,
    page_size: 20
  })
  const [paginacion, setPaginacion] = useState({
    total: 0,
    total_pages: 0
  })
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    cargarEnvios()
  }, [filtros.page, filtros.page_size, filtros.estado])

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filtros.search !== undefined) {
        cargarEnvios()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [filtros.search])

  const cargarEnvios = async () => {
    setLoading(true)
    try {
      const params = {
        page: filtros.page,
        page_size: filtros.page_size,
        ...(filtros.search && { search: filtros.search }),
        ...(filtros.estado && { estado: filtros.estado })
      }
      const response = await whatsappApi.getEnvios(params)
      setEnvios(response.results)
      setPaginacion({
        total: response.count,
        total_pages: response.total_pages
      })
    } catch (error) {
      console.error('Error cargando envíos:', error)
      toast.error('Error al cargar el historial de envíos')
    } finally {
      setLoading(false)
    }
  }

  const getEstadoBadge = (estado: Envio['estado']) => {
    const badges = {
      enviado: { label: 'Enviado', className: 'badge-gradient hover:opacity-90' },
      enviando: { label: 'Enviando', className: 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-500' },
      error: { label: 'Error', className: 'bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400' },
      parcial: { label: 'Parcial', className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400' },
      pendiente: { label: 'Pendiente', className: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' }
    }
    return badges[estado] || badges.pendiente
  }

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <History className="h-6 w-6 text-black dark:text-white" />
            Historial De Envíos
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Seguimiento Y Estadísticas De Mensajes Enviados
            </span>
          )}
        </div>
        <Link to="/whatsapp/envio-masivo">
          <Button className="bg-teal-800 hover:bg-teal-900 text-white">
            <Send className="h-4 w-4 mr-2" />
            Nuevo Envio
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="search">Buscar Por Asunto</Label>
              <Input
                id="search"
                placeholder="Buscar..."
                value={filtros.search}
                onChange={(e) => setFiltros({ ...filtros, search: e.target.value, page: 1 })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estado">Estado</Label>
              <Select
                value={filtros.estado || 'all'}
                onValueChange={(value) => setFiltros({ ...filtros, estado: value === 'all' ? '' : value, page: 1 })}
              >
                <SelectTrigger id="estado">
                  <SelectValue placeholder="Todos Los Estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Los Estados</SelectItem>
                  <SelectItem value="enviado">Enviado</SelectItem>
                  <SelectItem value="enviando">Enviando</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="parcial">Parcial</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button variant="ghost" size="icon" onClick={cargarEnvios}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de envíos */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            Envios
            {paginacion.total > 0 && (
              <Badge className="badge-gradient hover:opacity-90">
                {paginacion.total}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
                <p className="text-sm text-muted-foreground">Cargando Envíos...</p>
              </div>
            </div>
          ) : envios.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-muted-foreground">No Se Encontraron Envíos</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
                    <TableRow>
                      <TableHead><div className="flex items-center gap-2"><FileText className="h-4 w-4" />Asunto</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Layout className="h-4 w-4" />Plantilla</div></TableHead>
                      <TableHead className="text-center"><div className="flex items-center justify-center gap-2"><Users className="h-4 w-4" />Destinatarios</div></TableHead>
                      <TableHead className="text-center"><div className="flex items-center justify-center gap-2"><CheckCircle className="h-4 w-4" />Exitosos</div></TableHead>
                      <TableHead className="text-center"><div className="flex items-center justify-center gap-2"><XCircle className="h-4 w-4" />Errores</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><TrendingUp className="h-4 w-4" />Tasa De Éxito</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Activity className="h-4 w-4" />Estado</div></TableHead>
                      <TableHead><div className="flex items-center gap-2"><Calendar className="h-4 w-4" />Fecha</div></TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {envios.map((envio) => {
                      const badge = getEstadoBadge(envio.estado)
                      return (
                        <TableRow key={envio.id}>
                          <TableCell className="font-medium capitalize">{envio.asunto}</TableCell>
                          <TableCell>
                            {envio.plantilla ? (
                              <Badge variant="secondary" className="capitalize">{envio.plantilla.nombre}</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">Sin Plantilla</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">{envio.total_destinatarios}</TableCell>
                          <TableCell className="text-center text-teal-600 dark:text-teal-400 font-medium">
                            {envio.enviados_exitosos}
                          </TableCell>
                          <TableCell className="text-center text-red-600 font-medium">
                            {envio.errores}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="h-2 w-full rounded-full bg-teal-500/10">
                                <div
                                  className="h-2 rounded-full bg-teal-500 transition-all"
                                  style={{ width: `${envio.tasa_exito}%` }}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground text-center">
                                {envio.tasa_exito.toFixed(1)}%
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={badge.className}>
                              {badge.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatearFecha(envio.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Link to={`/whatsapp/analytics/${envio.id}`}>
                              <Button variant="ghost" size="sm">
                                <BarChart3 className="h-4 w-4" />
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Mostrar</span>
                  <Select
                    value={filtros.page_size.toString()}
                    onValueChange={(value) => setFiltros({ ...filtros, page: 1, page_size: parseInt(value) })}
                  >
                    <SelectTrigger className="w-[80px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">Por Página</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setFiltros({ ...filtros, page: 1 })}
                    disabled={filtros.page === 1}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setFiltros({ ...filtros, page: filtros.page - 1 })}
                    disabled={filtros.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[100px] text-center">
                    {filtros.page} de {paginacion.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setFiltros({ ...filtros, page: filtros.page + 1 })}
                    disabled={filtros.page === paginacion.total_pages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setFiltros({ ...filtros, page: paginacion.total_pages })}
                    disabled={filtros.page === paginacion.total_pages}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
